import { getHeadsetPollers } from 'resources/headsets/headset-pollers';
import PlantronicsHeadset from 'resources/headsets/plantronics/plantronics-headset';
import EposHeadset from 'resources/headsets/epos/epos-headset';
import { Manufacturers } from 'resources/headsets/headset-utils';
import { connectToPlantronicsHub } from 'resources/headsets/plantronics/plantronics-service';
import { HeadsetCallbacks } from 'resources/headsets/headset-callbacks';
import { store } from 'flow/configure-store';
import Jabra4Headset from 'resources/headsets/jabra4/jabra4-headset';
import YealinkHeadset from 'resources/headsets/yealink/yealink-headset';

let findingHeadsetInProgress = false;

class HeadsetFinder {
    /**
     * @returns An Promise around an instance of {@link HeadsetInterface}.
     * If no headset, or more than one, was found during polling, the Promise is rejected.
     * The Promise is also rejected if the connection attempt to the headset fails.
     * Rejections of the promise needs to be handled by the caller of this function.
     */
    static async findHeadset() {
        if (findingHeadsetInProgress) {
            return Promise.reject('Already trying to find a headset');
        }
        findingHeadsetInProgress = true;
        try {
            return await tryFindHeadset();
        } catch (e) {
            return Promise.reject(e);
        } finally {
            findingHeadsetInProgress = false;
        }
    }
}

const tryFindHeadset = async () => {
    return Promise.all(getHeadsetPollers().map((poller) => poller.poll())).then(
        (deviceInfoArray) => {
            const foundHeadsets = deviceInfoArray.filter((e) => !!e);
            // We'll only handle one headset at a time, for now.
            // Support for more headsets is considered in WEB-1825.
            if (foundHeadsets.length === 0) {
                return Promise.reject('No headsets found');
            } else if (foundHeadsets.length > 1) {
                return Promise.reject('More than one headset found');
            }
            const deviceInfo = foundHeadsets[0];
            return connectToHeadset(deviceInfo).then(() =>
                createHeadset(deviceInfo),
            );
        },
    );
};

const connectToHeadset = (deviceInfo) => {
    if (deviceInfo.ManufacturerName === Manufacturers.Plantronics.key) {
        return connectToPlantronicsHub(deviceInfo);
    } else if (deviceInfo.ManufacturerName === Manufacturers.EPOS.key) {
        return Promise.resolve();
    } else if (deviceInfo.ManufacturerName === Manufacturers.Jabra4.key) {
        return Promise.resolve();
    } else if (deviceInfo.ManufacturerName === Manufacturers.Yealink.key) {
        return Promise.resolve();
    } else {
        return Promise.reject();
    }
};

const createHeadset = (deviceInfo) => {
    if (deviceInfo.ManufacturerName === Manufacturers.Plantronics.key) {
        return new PlantronicsHeadset(deviceInfo).attachCallbacks(
            HeadsetCallbacks(store.dispatch, store.getState),
        );
    } else if (deviceInfo.ManufacturerName === Manufacturers.EPOS.key) {
        return new EposHeadset(deviceInfo).attachCallbacks(
            HeadsetCallbacks(store.dispatch, store.getState),
        );
    } else if (deviceInfo.ManufacturerName === Manufacturers.Jabra4.key) {
        return new Jabra4Headset(deviceInfo).attachCallbacks(
            HeadsetCallbacks(store.dispatch, store.getState),
        );
    } else if (deviceInfo.ManufacturerName === Manufacturers.Yealink.key) {
        return new YealinkHeadset(deviceInfo).attachCallbacks(
            HeadsetCallbacks(store.dispatch, store.getState),
        );
    } else {
        // No headsets of other types
        return null;
    }
};

export default HeadsetFinder;
